import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CalendarClock, MapPin, IndianRupee, Star, FileDown, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const STATUS_COLORS = {
  pending_payment: "bg-brand-yellow/20 text-brand-ink",
  confirmed: "bg-brand-cyan/20 text-brand-ink",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-muted text-muted-foreground",
};

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [luckyReward, setLuckyReward] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/bookings").then(({ data }) => setItems(data)).finally(() => setLoading(false));
    api.get("/lucky/mine").then(({ data }) => setLuckyReward(data.reward)).catch(() => {});
  };
  useEffect(load, []);

  const grouped = {
    upcoming: items.filter((b) => ["pending_payment", "confirmed"].includes(b.status)),
    completed: items.filter((b) => b.status === "completed"),
    cancelled: items.filter((b) => b.status === "cancelled"),
  };

  const awaitingReview = useMemo(
    () => grouped.completed.filter((b) => !b.has_review),
    [grouped.completed],
  );

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="my-bookings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">My bookings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Track past, upcoming and cancelled services.</p>
      </div>

      {luckyReward && (
        <div
          className="relative overflow-hidden rounded-2xl border border-brand-orange/30 bg-gradient-to-br from-brand-orange/15 via-brand-yellow/10 to-white p-4 sm:p-5"
          data-testid="lucky-reward-banner"
        >
          <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-orange/25 blur-2xl" />
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand-orange text-white shadow-lg shadow-brand-orange/40">
                <Gift size={20} />
              </div>
              <div>
                <div className="font-heading text-base font-bold sm:text-lg">
                  You won <span className="text-brand-orange">Rs. {luckyReward.amount} off</span> your next service!
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Applied automatically the moment you book. Coupon ref: <b className="font-mono">{luckyReward.code}</b>
                </div>
              </div>
            </div>
            <Link to="/browse" data-testid="lucky-browse-btn">
              <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90">
                Redeem now
              </Button>
            </Link>
          </div>
        </div>
      )}

      {awaitingReview.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand-yellow/40 bg-brand-yellow/15 p-4 sm:p-5"
          data-testid="review-cta-banner"
        >
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-yellow text-brand-ink">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-heading text-base font-bold">
                Your feedback shapes better service
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {awaitingReview.length === 1
                  ? "1 completed service is waiting for your rating — it takes under a minute."
                  : `${awaitingReview.length} completed services are waiting for your rating — each takes under a minute.`}
              </div>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-widest text-brand-orange font-bold">
            Scroll down to rate ↓
          </div>
        </div>
      )}

      <Tabs defaultValue={awaitingReview.length > 0 ? "completed" : "upcoming"}>
        <TabsList data-testid="bookings-tabs">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({grouped.upcoming.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed ({grouped.completed.length})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled ({grouped.cancelled.length})</TabsTrigger>
        </TabsList>

        {["upcoming", "completed", "cancelled"].map((k) => (
          <TabsContent key={k} value={k} className="mt-5 space-y-3">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : grouped[k].length === 0 ? (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                No {k} bookings yet.
              </div>
            ) : grouped[k].map((b) => (
              <BookingRow key={b.id} b={b} onCancel={cancel} onReviewed={load} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function BookingRow({ b, onCancel, onReviewed }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submitReview = async () => {
    setSubmitting(true);
    try {
      await api.post(`/bookings/${b.id}/review`, { rating, comment });
      toast.success("Thanks for your feedback!");
      setOpen(false);
      onReviewed?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const needsReview = b.status === "completed" && !b.has_review;
  const rowBorder = needsReview
    ? "border-brand-yellow/60 ring-1 ring-brand-yellow/40 bg-brand-yellow/5"
    : "border-foreground/10 bg-white";

  return (
    <div className={`rounded-2xl border p-4 sm:p-5 ${rowBorder}`} data-testid={`booking-${b.id}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <img src={b.service?.image_url} alt={b.service?.title} className="h-24 w-full rounded-xl object-cover sm:h-20 sm:w-32" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[b.status] || "bg-muted"}`}>
              {b.status.replace("_", " ")}
            </span>
            {needsReview && (
              <span
                className="rounded-full bg-brand-orange px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
                data-testid={`feedback-needed-${b.id}`}
              >
                Feedback needed
              </span>
            )}
            {b.has_review && (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800">
                <Star size={10} className="fill-green-700 text-green-700" /> Reviewed · {b.review?.rating}★
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">Ref {b.payment_ref}</span>
          </div>
          <div className="mt-1 font-heading text-base font-bold">{b.service?.title}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><CalendarClock size={12} /> {new Date(b.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {b.address}</span>
            <span className="flex items-center gap-1"><IndianRupee size={12} /> {b.amount}</span>
            {b.lucky_reward_amount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 font-semibold text-brand-orange" data-testid={`lucky-applied-${b.id}`}>
                <Gift size={11} /> −Rs.{b.lucky_reward_amount} lucky discount
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {b.payment_status === "paid" && (
            <a
              href={`${BACKEND_URL}/api/bookings/${b.id}/receipt`}
              target="_blank" rel="noreferrer"
              data-testid={`receipt-btn-${b.id}`}
            >
              <Button variant="outline" className="rounded-full">
                <FileDown size={14} className="mr-1.5" /> Receipt
              </Button>
            </a>
          )}
          {b.status === "pending_payment" && (
            <Link to={`/payment/${b.id}`} data-testid={`pay-btn-${b.id}`}>
              <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90">Complete payment</Button>
            </Link>
          )}
          {b.status === "confirmed" && (
            <Button variant="outline" className="rounded-full" onClick={() => onCancel(b.id)} data-testid={`cancel-btn-${b.id}`}>
              Cancel
            </Button>
          )}
          {needsReview && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
                  data-testid={`review-btn-${b.id}`}
                >
                  <Star size={14} className="mr-1.5" /> Rate your experience
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>How was {b.service?.title}?</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                  Your rating helps other customers and lets us improve service quality.
                </p>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      data-testid={`star-${n}`}
                      className="p-1"
                    >
                      <Star size={28} className={n <= rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground"} />
                    </button>
                  ))}
                  <span className="ml-2 text-sm font-semibold">{rating}/5</span>
                </div>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us what worked well or what we can improve (optional)"
                  rows={4}
                  data-testid="review-comment"
                />
                <DialogFooter>
                  <Button
                    onClick={submitReview}
                    disabled={submitting}
                    className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
                    data-testid="submit-review"
                  >
                    {submitting ? "Sending..." : "Submit feedback"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
}
